import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Browser } from '@capacitor/browser';
import './staff-design-system.css';
import './Staff.css';
import './StaffCarteHebdo.css';
import { SectionHeader } from '../../components/staff/ui/SectionHeader';
import { apiFetch, BACKEND_URL } from '../../utils/api';

type Status = 'idle' | 'loading' | 'success' | 'error';

interface Section {
  key: 'entrees' | 'plats' | 'desserts';
  label: string;
  prix: string;
}

const SECTIONS: Section[] = [
  { key: 'entrees', label: 'Entrées', prix: '7 €' },
  { key: 'plats', label: 'Plats', prix: '14 €' },
  { key: 'desserts', label: 'Desserts', prix: '7 €' },
];

const CHAMPS_PAR_SECTION = 3;
const SEUIL_GENERATION = 7;

/** Texte vide initial : 3 champs par section. */
function emptyState(): Record<Section['key'], string[]> {
  return {
    entrees: ['', '', ''],
    plats: ['', '', ''],
    desserts: ['', '', ''],
  };
}

export function StaffCarteHebdoScreen() {
  const navigate = useNavigate();
  const [valeurs, setValeurs] = useState<Record<Section['key'], string[]>>(emptyState);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const nbRemplis = useMemo(
    () =>
      SECTIONS.reduce(
        (total, s) => total + valeurs[s.key].filter((v) => v.trim().length > 0).length,
        0,
      ),
    [valeurs],
  );

  const peutGenerer = nbRemplis >= SEUIL_GENERATION && status !== 'loading';

  const setChamp = (key: Section['key'], idx: number, value: string) => {
    setValeurs((prev) => {
      const next = { ...prev, [key]: [...prev[key]] };
      next[key][idx] = value;
      return next;
    });
  };

  const effacer = () => {
    setValeurs(emptyState());
    setStatus('idle');
    setErrorMsg(null);
    setPdfUrl(null);
  };

  const generer = async () => {
    if (!peutGenerer) return;
    setStatus('loading');
    setErrorMsg(null);
    setPdfUrl(null);

    // 9 champs distincts envoyés à plat (E1..E3 / P1..P3 / D1..D3).
    const payload = {
      E1: valeurs.entrees[0].trim(), E2: valeurs.entrees[1].trim(), E3: valeurs.entrees[2].trim(),
      P1: valeurs.plats[0].trim(), P2: valeurs.plats[1].trim(), P3: valeurs.plats[2].trim(),
      D1: valeurs.desserts[0].trim(), D2: valeurs.desserts[1].trim(), D3: valeurs.desserts[2].trim(),
    };

    try {
      const res = await apiFetch(`/carte/generer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const map: Record<string, string> = {
          render_failed: 'La génération du PDF a échoué. Réessayez dans un instant.',
        };
        throw new Error(map[err?.error] ?? err?.message ?? err?.error ?? `Erreur ${res.status}`);
      }

      const data = (await res.json()) as { pdfUrl?: string };
      if (!data.pdfUrl) throw new Error('Réponse serveur sans URL de PDF.');
      // Le backend renvoie un chemin relatif (/carte/pdf/<id>) ; on construit
      // l'URL absolue pour @capacitor/browser (la visionneuse native doit avoir une URL https).
      setPdfUrl(data.pdfUrl.startsWith('http') ? data.pdfUrl : `${BACKEND_URL}${data.pdfUrl}`);
      setStatus('success');
    } catch (e: any) {
      setErrorMsg(e?.message ?? 'Erreur inattendue');
      setStatus('error');
    }
  };

  return (
    <div className="staff-page-v2 staff-page">
      <div className="staff-header-v2">
        <button className="staff-back-v2" onClick={() => navigate('/staff/dashboard')}>‹</button>
        <h1>Carte hebdo</h1>
        <span className="staff-badge-v2">{nbRemplis}/9 plats saisis</span>
      </div>

      {status === 'error' && errorMsg && (
        <div className="carte-banner carte-banner-error" role="alert">
          {errorMsg}
        </div>
      )}

      {status === 'success' && pdfUrl && (
        <div className="carte-banner carte-banner-success">
          <span>Carte générée avec succès.</span>
          {/* href conservé pour le fallback web / l'accessibilité, mais le clic
              passe par @capacitor/browser : la WKWebView iOS ignore les liens
              data:/download, alors que Browser.open ouvre la visionneuse PDF native. */}
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.preventDefault();
              void Browser.open({ url: pdfUrl });
            }}
          >
            Ouvrir le PDF
          </a>
        </div>
      )}

      {SECTIONS.map((section) => (
        <div className="staff-section-v2" key={section.key}>
          <SectionHeader title={section.label} caption={section.prix} />
          <div className="carte-fields">
            {valeurs[section.key].map((val, idx) => {
              const optionnel = idx === CHAMPS_PAR_SECTION - 1;
              return (
                <textarea
                  key={idx}
                  className="carte-field"
                  rows={2}
                  value={val}
                  onChange={(e) => setChamp(section.key, idx, e.target.value)}
                  placeholder={`${section.label.slice(0, -1)} ${idx + 1}${optionnel ? ' (optionnel)' : ''}`}
                />
              );
            })}
          </div>
        </div>
      ))}

      <div className="carte-footer">
        <button type="button" className="carte-btn carte-btn-ghost" onClick={effacer} disabled={status === 'loading'}>
          Effacer
        </button>
        <button type="button" className="carte-btn carte-btn-primary" onClick={generer} disabled={!peutGenerer}>
          {status === 'loading' ? 'Génération en cours…' : 'Générer la carte →'}
        </button>
      </div>

      {nbRemplis < SEUIL_GENERATION && (
        <p className="carte-hint">
          Renseignez au moins {SEUIL_GENERATION} plats pour générer la carte.
        </p>
      )}
    </div>
  );
}
